// SkillTemplatesModal — Picker de templates de skills pro composer.
//
// Duas seções:
//   1. Meus templates — criados pelo user (persistidos em localStorage)
//   2. Combos prontos — curated, hardcoded
//   3. Skills individuais — slash commands carregadas via API
//
// Multi-select; click "Inserir" dispara `apolo:insertComposer` com texto
// concatenado: [slashes...] + body opcional, pronto pro composer.
//
// Modo edit: form interno pra criar/editar template custom — emoji, título,
// descrição, skills (multi-pick), body de texto extra, tags.

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, Copy, Pencil, Plus, Search, Sparkles, Trash2, X } from 'lucide-react';
import { useSlashCommands } from '../../hooks/useSlashCommands.js';
import { useCustomTemplates } from '../../hooks/useCustomTemplates.js';

// Combos prontos — hardcoded. User pode duplicar pra editar (vira custom).
const CURATED_TEMPLATES = [
  {
    id: 'brainstorm-plan',
    emoji: '🧠',
    title: 'Brainstorm + Plan',
    description: 'Explora requisitos, depois escreve plano executável',
    slashes: ['/superpowers:brainstorming', '/superpowers:writing-plans'],
    body: '',
    tags: ['plan'],
  },
  {
    id: 'sprint-full',
    emoji: '🚀',
    title: 'Sprint completo',
    description: 'Diretiva de sprint + dispatch paralelo de agents',
    slashes: ['/anthropic-skills:sprint-directive-prompt', '/superpowers:dispatching-parallel-agents'],
    body: '',
    tags: ['plan', 'execution'],
  },
  {
    id: 'design-review',
    emoji: '🎨',
    title: 'Design review completo',
    description: 'Audit visual + clareza UI + acessibilidade',
    slashes: ['/plan-design-review', '/ui-clarity-auditor:ui-audit', '/accessibility-agents:audit'],
    body: '',
    tags: ['design'],
  },
  {
    id: 'paranoid-review',
    emoji: '🔍',
    title: 'Paranoid review',
    description: 'Code review staff + security + correctness',
    slashes: ['/review', '/code-review', '/security-review'],
    body: '',
    tags: ['review'],
  },
  {
    id: 'ship',
    emoji: '📦',
    title: 'Ship',
    description: 'Test + commit + PR + docs',
    slashes: ['/ship', '/document-release'],
    body: '',
    tags: ['ship'],
  },
  {
    id: 'debug-full',
    emoji: '🐛',
    title: 'Debug sistemático',
    description: 'Reproduzir → minimizar → hipótese → fix → regression test',
    slashes: ['/superpowers:systematic-debugging', '/mattpocock-skills:diagnose'],
    body: '',
    tags: ['debug'],
  },
  {
    id: 'triple-plan',
    emoji: '🎯',
    title: 'Triple review (CEO + Eng + Design)',
    description: 'Três perspectivas sobre o mesmo plano',
    slashes: ['/plan-ceo-review', '/plan-eng-review', '/plan-design-review'],
    body: '',
    tags: ['plan', 'review'],
  },
  {
    id: 'tdd',
    emoji: '🧪',
    title: 'TDD full',
    description: 'Red-green-refactor disciplinado',
    slashes: ['/superpowers:test-driven-development', '/mattpocock-skills:tdd'],
    body: '',
    tags: ['test'],
  },
  {
    id: 'polish-ui',
    emoji: '💎',
    title: 'Polish UI',
    description: 'Polimento + microinteractions + clareza visual',
    slashes: ['/impeccable:impeccable polish', '/anthropic-skills:microinteractions-mastery', '/tastedesign'],
    body: '',
    tags: ['design'],
  },
  {
    id: 'verify',
    emoji: '✅',
    title: 'Verify completo',
    description: 'Roda evidência antes de declarar pronto',
    slashes: ['/superpowers:verification-before-completion', '/verify'],
    body: '',
    tags: ['test'],
  },
  {
    id: 'brain-context',
    emoji: '🧠',
    title: 'Carregar Brain + Plan',
    description: 'Carrega contexto do vault Obsidian e depois planeja',
    slashes: ['/anthropic-skills:pedro-brain', '/superpowers:writing-plans'],
    body: '',
    tags: ['brain', 'plan'],
  },
  {
    id: 'qa-cycle',
    emoji: '🧪',
    title: 'QA cycle',
    description: 'Testa app no browser, encontra bugs, conserta',
    slashes: ['/qa'],
    body: '',
    tags: ['test'],
  },
];

const TAG_FILTERS = [
  { id: 'all',       label: 'Todos' },
  { id: 'plan',      label: 'Plan' },
  { id: 'review',    label: 'Review' },
  { id: 'design',    label: 'Design' },
  { id: 'debug',     label: 'Debug' },
  { id: 'test',      label: 'Test' },
  { id: 'ship',      label: 'Ship' },
  { id: 'brain',     label: 'Brain' },
];

const EMOJI_SUGGESTIONS = ['🧩', '🧠', '🚀', '🎨', '🔍', '📦', '🐛', '🎯', '🧪', '💎', '✅', '📝', '⚡', '🔥', '🌟', '🛠️', '📊', '🎬', '🔧', '⚙️'];

// ── Form de criação/edição ───────────────────────────────────────────
function CustomTemplateForm({ initial, onSave, onCancel, commands }) {
  const [emoji, setEmoji] = useState(initial?.emoji || '🧩');
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [slashes, setSlashes] = useState(initial?.slashes || []);
  const [body, setBody] = useState(initial?.body || '');
  const [tagsInput, setTagsInput] = useState((initial?.tags || []).join(', '));
  const [skillQuery, setSkillQuery] = useState('');
  const [error, setError] = useState(null);

  const filtered = useMemo(() => {
    const q = skillQuery.trim().toLowerCase();
    if (!q) return commands.slice(0, 20);
    return commands.filter((c) =>
      c.slash_command.toLowerCase().includes(q)
      || (c.display_name || '').toLowerCase().includes(q)
      || (c.description || '').toLowerCase().includes(q),
    ).slice(0, 30);
  }, [commands, skillQuery]);

  function toggleSlash(slash) {
    setSlashes((prev) => prev.includes(slash) ? prev.filter((s) => s !== slash) : [...prev, slash]);
  }

  function reorder(idx, dir) {
    setSlashes((prev) => {
      const next = [...prev];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= next.length) return prev;
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  }

  function handleSubmit(e) {
    e?.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) { setError('Título obrigatório'); return; }
    if (slashes.length === 0 && !body.trim()) {
      setError('Adicione pelo menos uma skill ou um texto');
      return;
    }
    setError(null);
    const tags = tagsInput.split(',').map((s) => s.trim()).filter(Boolean);
    onSave({
      id: initial?.id,
      emoji: emoji || '🧩',
      title: trimmedTitle,
      description: description.trim(),
      slashes,
      body: body.trim(),
      tags,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {error && (
          <div role="alert" className="rounded-md border border-apolo-error/30 bg-apolo-error/10 px-2.5 py-1.5 text-[11px] text-apolo-error">
            {error}
          </div>
        )}

        {/* Emoji + Título */}
        <div className="flex gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-medium uppercase tracking-wider text-apolo-textTertiary">Emoji</label>
            <input
              type="text"
              value={emoji}
              onChange={(e) => setEmoji(e.target.value.slice(0, 2))}
              maxLength={4}
              className="h-9 w-14 rounded-md border border-apolo-border bg-apolo-surface3 text-center text-xl focus:border-apolo-accent focus:outline-none"
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <label className="text-[10px] font-medium uppercase tracking-wider text-apolo-textTertiary">Título</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              placeholder="Ex: Sprint de feature nova"
              className="h-9 rounded-md border border-apolo-border bg-apolo-surface3 px-2.5 text-[12px] text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
            />
          </div>
        </div>

        {/* Sugestões de emoji */}
        <div className="flex flex-wrap gap-1">
          {EMOJI_SUGGESTIONS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setEmoji(e)}
              className={
                'h-6 w-6 rounded text-sm transition ' +
                (emoji === e ? 'bg-apolo-accent/20 ring-1 ring-apolo-accent' : 'hover:bg-apolo-surface2')
              }
            >
              {e}
            </button>
          ))}
        </div>

        {/* Descrição */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-apolo-textTertiary">Descrição (opcional)</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="O que esse template faz?"
            className="h-9 rounded-md border border-apolo-border bg-apolo-surface3 px-2.5 text-[12px] text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
          />
        </div>

        {/* Skills selecionadas (com reorder) */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-apolo-textTertiary">
            Skills ({slashes.length})
          </label>
          {slashes.length > 0 && (
            <ul className="flex flex-col gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2/40 p-1.5">
              {slashes.map((s, idx) => (
                <li key={s} className="flex items-center gap-1.5 rounded bg-apolo-surface3 px-2 py-1">
                  <span className="text-[10px] text-apolo-textTertiary">{idx + 1}.</span>
                  <code className="flex-1 truncate font-mono text-[11px] text-apolo-textPrimary">{s}</code>
                  <button
                    type="button"
                    onClick={() => reorder(idx, -1)}
                    disabled={idx === 0}
                    className="text-[10px] text-apolo-textTertiary transition hover:text-apolo-textPrimary disabled:opacity-30"
                    aria-label="Mover pra cima"
                  >↑</button>
                  <button
                    type="button"
                    onClick={() => reorder(idx, 1)}
                    disabled={idx === slashes.length - 1}
                    className="text-[10px] text-apolo-textTertiary transition hover:text-apolo-textPrimary disabled:opacity-30"
                    aria-label="Mover pra baixo"
                  >↓</button>
                  <button
                    type="button"
                    onClick={() => toggleSlash(s)}
                    className="text-apolo-textTertiary transition hover:text-apolo-error"
                    aria-label="Remover"
                  >
                    <X size={10} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Picker de skills disponíveis */}
          <div className="relative">
            <Search size={11} aria-hidden="true" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-apolo-textTertiary" />
            <input
              type="text"
              value={skillQuery}
              onChange={(e) => setSkillQuery(e.target.value)}
              placeholder="Buscar skill pra adicionar…"
              className="h-7 w-full rounded-md border border-apolo-border bg-apolo-surface3 pl-7 pr-2 text-[11px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
            />
          </div>
          <ul className="max-h-40 overflow-y-auto rounded-md border border-apolo-borderSubtle">
            {filtered.length === 0 && (
              <li className="px-2.5 py-1.5 text-[10.5px] text-apolo-textTertiary">Nenhuma skill encontrada</li>
            )}
            {filtered.map((cmd) => {
              const isPicked = slashes.includes(cmd.slash_command);
              return (
                <li key={cmd.slash_command}>
                  <button
                    type="button"
                    onClick={() => toggleSlash(cmd.slash_command)}
                    className={
                      'flex w-full items-start gap-2 px-2.5 py-1.5 text-left text-[11px] transition ' +
                      (isPicked ? 'bg-apolo-accent/10' : 'hover:bg-apolo-surface2')
                    }
                  >
                    <span
                      className={
                        'mt-0.5 inline-flex h-3 w-3 shrink-0 items-center justify-center rounded border ' +
                        (isPicked ? 'border-apolo-accent bg-apolo-accent text-apolo-bg' : 'border-apolo-borderStrong')
                      }
                      aria-hidden="true"
                    >
                      {isPicked && <Check size={8} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <code className="block truncate font-mono text-[11px] font-semibold text-apolo-textPrimary">
                        {cmd.slash_command}
                      </code>
                      {cmd.description && (
                        <p className="line-clamp-1 text-[10px] text-apolo-textTertiary">{cmd.description}</p>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Body de texto extra */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-apolo-textTertiary">
            Texto adicional (opcional)
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Texto que vai ser anexado depois das skills. Ex: '— foco em performance, ignore acessibilidade por agora'"
            className="resize-y rounded-md border border-apolo-border bg-apolo-surface3 px-2.5 py-1.5 font-mono text-[11px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
          />
        </div>

        {/* Tags */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-apolo-textTertiary">
            Tags (separadas por vírgula)
          </label>
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="plan, review, design"
            className="h-7 rounded-md border border-apolo-border bg-apolo-surface3 px-2.5 text-[11px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
          />
        </div>
      </div>

      <footer className="flex items-center justify-between border-t border-apolo-borderSubtle bg-apolo-surface2/40 px-4 py-2.5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-2.5 py-1 text-[11px] text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="inline-flex items-center gap-1.5 rounded-md bg-apolo-accent px-3 py-1 text-[11px] font-medium text-apolo-bg transition hover:bg-apolo-accent/90"
        >
          <Check size={10} aria-hidden="true" />
          {initial?.id ? 'Salvar alterações' : 'Criar template'}
        </button>
      </footer>
    </form>
  );
}

// ── Card de template (reusado por curated + custom) ──────────────────
function TemplateCard({ template, selectedSet, onPick, onEdit, onDuplicate, onDelete }) {
  const allSelected = template.slashes.length > 0 && template.slashes.every((s) => selectedSet.has(s));
  return (
    <div
      className={
        'group/tpl relative overflow-hidden rounded-lg border p-3 transition ' +
        (allSelected
          ? 'border-apolo-accent bg-apolo-accent/10'
          : 'border-apolo-borderSubtle bg-apolo-surface2/40 hover:border-apolo-borderStrong hover:bg-apolo-surface2')
      }
    >
      <button
        type="button"
        onClick={() => onPick(template)}
        className="block w-full text-left"
      >
        <div className="mb-1 flex items-center gap-2">
          <span aria-hidden="true" className="text-base">{template.emoji}</span>
          <span className="text-[12.5px] font-semibold text-apolo-textPrimary">{template.title}</span>
          {allSelected && (
            <Check size={11} aria-hidden="true" className="ml-auto text-apolo-accent" />
          )}
        </div>
        {template.description && (
          <p className="mb-1.5 line-clamp-2 text-[11.5px] leading-relaxed text-apolo-textSecondary">
            {template.description}
          </p>
        )}
        {template.slashes.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {template.slashes.slice(0, 4).map((s) => (
              <code key={s} className="rounded bg-apolo-surface3 px-1.5 py-0.5 font-mono text-[9.5px] text-apolo-textTertiary">
                {s}
              </code>
            ))}
            {template.slashes.length > 4 && (
              <span className="text-[9.5px] text-apolo-textTertiary">+{template.slashes.length - 4}</span>
            )}
          </div>
        )}
        {template.body && (
          <p className="mt-1.5 line-clamp-1 rounded border border-apolo-borderSubtle bg-apolo-surface3 px-1.5 py-0.5 font-mono text-[10px] text-apolo-textTertiary">
            “{template.body}”
          </p>
        )}
      </button>
      {/* Ações — só aparecem on hover (custom: edit/delete, curated: duplicar) */}
      <div className="absolute right-2 top-2 flex gap-0.5 opacity-0 transition-opacity group-hover/tpl:opacity-100">
        {onEdit && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(template); }}
            aria-label="Editar template"
            title="Editar"
            className="inline-flex h-5 w-5 items-center justify-center rounded bg-apolo-surface3 text-apolo-textTertiary transition hover:text-apolo-textPrimary"
          >
            <Pencil size={9} />
          </button>
        )}
        {onDuplicate && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDuplicate(template); }}
            aria-label="Duplicar"
            title="Duplicar"
            className="inline-flex h-5 w-5 items-center justify-center rounded bg-apolo-surface3 text-apolo-textTertiary transition hover:text-apolo-textPrimary"
          >
            <Copy size={9} />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(template); }}
            aria-label="Excluir"
            title="Excluir"
            className="inline-flex h-5 w-5 items-center justify-center rounded bg-apolo-surface3 text-apolo-textTertiary transition hover:text-apolo-error"
          >
            <Trash2 size={9} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────
export default function SkillTemplatesModal({ open, onClose }) {
  const [view, setView] = useState('list'); // 'list' | 'edit'
  const [editing, setEditing] = useState(null); // template em edição (null = novo)
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [bodyExtras, setBodyExtras] = useState([]); // bodies dos templates selecionados (em ordem)
  const { commands, loading } = useSlashCommands();
  const { templates: customTemplates, save: saveCustom, remove: removeCustom } = useCustomTemplates();

  // Reset state on open
  useEffect(() => {
    if (open) {
      setView('list');
      setEditing(null);
      setQuery('');
      setActiveTag('all');
      setSelected(new Set());
      setBodyExtras([]);
    }
  }, [open]);

  // Escape pra fechar
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        if (view === 'edit') {
          setView('list');
          setEditing(null);
        } else {
          onClose?.();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, view]);

  const filteredCustom = useMemo(() => {
    const q = query.trim().toLowerCase();
    return customTemplates.filter((t) => {
      if (activeTag !== 'all' && !(t.tags || []).includes(activeTag)) return false;
      if (!q) return true;
      return (t.title || '').toLowerCase().includes(q)
        || (t.description || '').toLowerCase().includes(q)
        || (t.body || '').toLowerCase().includes(q)
        || (t.slashes || []).some((s) => s.toLowerCase().includes(q));
    });
  }, [customTemplates, query, activeTag]);

  const filteredCurated = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CURATED_TEMPLATES.filter((t) => {
      if (activeTag !== 'all' && !t.tags.includes(activeTag)) return false;
      if (!q) return true;
      return t.title.toLowerCase().includes(q)
        || t.description.toLowerCase().includes(q)
        || t.slashes.some((s) => s.toLowerCase().includes(q));
    });
  }, [query, activeTag]);

  const filteredCommands = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands.slice(0, 20);
    return commands.filter((c) =>
      c.slash_command.toLowerCase().includes(q)
      || (c.display_name || '').toLowerCase().includes(q)
      || (c.description || '').toLowerCase().includes(q),
    ).slice(0, 50);
  }, [commands, query]);

  function toggleSelection(key) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handlePickTemplate(template) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const s of template.slashes) next.add(s);
      return next;
    });
    if (template.body) {
      setBodyExtras((prev) => prev.includes(template.body) ? prev : [...prev, template.body]);
    }
  }

  function handleInsert() {
    if (selected.size === 0 && bodyExtras.length === 0) return;
    const items = [...selected];
    const parts = [];
    if (items.length > 0) parts.push(items.join(' '));
    if (bodyExtras.length > 0) parts.push(bodyExtras.join('\n\n'));
    const text = parts.join('\n\n') + ' ';
    window.dispatchEvent(new CustomEvent('apolo:insertComposer', {
      detail: { text },
    }));
    onClose?.();
  }

  function openNewTemplate() {
    setEditing(null);
    setView('edit');
  }

  function openEdit(template) {
    setEditing(template);
    setView('edit');
  }

  function handleSaveTemplate(tpl) {
    saveCustom(tpl);
    setView('list');
    setEditing(null);
  }

  function handleDeleteTemplate(template) {
    if (!confirm(`Excluir template "${template.title}"?`)) return;
    removeCustom(template.id);
  }

  function handleDuplicateCurated(template) {
    saveCustom({
      ...template,
      id: undefined, // força criar novo ID
      title: `${template.title} (cópia)`,
    });
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="skill-templates-title"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-apolo-overlay p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 shadow-2xl"
      >
        {/* Header */}
        <header className="flex items-center justify-between border-b border-apolo-borderSubtle px-4 py-3">
          <div className="flex items-center gap-2">
            {view === 'edit' && (
              <button
                type="button"
                onClick={() => { setView('list'); setEditing(null); }}
                aria-label="Voltar"
                className="rounded-md p-1 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
              >
                <ArrowLeft size={13} aria-hidden="true" />
              </button>
            )}
            <Sparkles size={14} aria-hidden="true" className="text-apolo-accent" />
            <h2 id="skill-templates-title" className="text-sm font-medium text-apolo-textPrimary">
              {view === 'edit'
                ? (editing?.id ? 'Editar template' : 'Novo template')
                : 'Templates de Skills'}
            </h2>
            {view === 'list' && selected.size > 0 && (
              <span className="rounded-full bg-apolo-accent/15 px-2 py-0.5 text-[10px] font-medium text-apolo-accent">
                {selected.size} selecionado(s)
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-md p-1 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </header>

        {view === 'edit' ? (
          <CustomTemplateForm
            initial={editing}
            commands={commands}
            onSave={handleSaveTemplate}
            onCancel={() => { setView('list'); setEditing(null); }}
          />
        ) : (
          <>
            {/* Busca + tags */}
            <div className="border-b border-apolo-borderSubtle bg-apolo-surface2/40 px-4 py-2.5">
              <div className="flex gap-1.5">
                <div className="relative flex-1">
                  <Search size={11} aria-hidden="true" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-apolo-textTertiary" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar template ou skill…"
                    autoFocus
                    className="h-7 w-full rounded-md border border-apolo-border bg-apolo-surface3 pl-7 pr-2 text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={openNewTemplate}
                  className="inline-flex items-center gap-1 rounded-md border border-apolo-accent/40 bg-apolo-accent/10 px-2.5 text-[11px] font-medium text-apolo-accent transition hover:bg-apolo-accent/20"
                >
                  <Plus size={11} aria-hidden="true" />
                  Novo template
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {TAG_FILTERS.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => setActiveTag(tag.id)}
                    className={
                      'rounded-md px-2 py-0.5 text-[10px] font-medium transition ' +
                      (activeTag === tag.id
                        ? 'bg-apolo-accent text-apolo-bg'
                        : 'bg-apolo-surface3 text-apolo-textTertiary hover:text-apolo-textPrimary')
                    }
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Body scroll */}
            <div className="flex-1 overflow-y-auto">
              {/* Custom templates */}
              {customTemplates.length > 0 && (
                <section className="px-4 py-3">
                  <h3 className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-apolo-textTertiary">
                    Meus templates
                    <span className="rounded-full bg-apolo-surface3 px-1.5 text-[9px] font-medium">{customTemplates.length}</span>
                  </h3>
                  {filteredCustom.length === 0 ? (
                    <p className="text-[11px] text-apolo-textTertiary">Nenhum dos seus templates bate com o filtro.</p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {filteredCustom.map((t) => (
                        <TemplateCard
                          key={t.id}
                          template={t}
                          selectedSet={selected}
                          onPick={handlePickTemplate}
                          onEdit={openEdit}
                          onDelete={handleDeleteTemplate}
                        />
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* Curated templates */}
              {filteredCurated.length > 0 && (
                <section className="px-4 py-3">
                  <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-apolo-textTertiary">
                    Combos prontos
                  </h3>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {filteredCurated.map((t) => (
                      <TemplateCard
                        key={t.id}
                        template={t}
                        selectedSet={selected}
                        onPick={handlePickTemplate}
                        onDuplicate={handleDuplicateCurated}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Skills individuais */}
              <section className="px-4 py-3">
                <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-apolo-textTertiary">
                  Skills individuais {commands.length > 0 && `(${filteredCommands.length}/${commands.length})`}
                </h3>
                {loading && (
                  <p className="text-[11px] text-apolo-textTertiary">Carregando…</p>
                )}
                {!loading && filteredCommands.length === 0 && (
                  <p className="text-[11px] text-apolo-textTertiary">Nenhuma skill encontrada.</p>
                )}
                <ul className="flex flex-col gap-1">
                  {filteredCommands.map((cmd) => {
                    const isSelected = selected.has(cmd.slash_command);
                    return (
                      <li key={cmd.slash_command}>
                        <button
                          type="button"
                          onClick={() => toggleSelection(cmd.slash_command)}
                          className={
                            'flex w-full items-start gap-2 rounded-md border px-2.5 py-1.5 text-left transition ' +
                            (isSelected
                              ? 'border-apolo-accent bg-apolo-accent/10'
                              : 'border-transparent hover:border-apolo-borderSubtle hover:bg-apolo-surface2')
                          }
                        >
                          <span
                            className={
                              'mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ' +
                              (isSelected
                                ? 'border-apolo-accent bg-apolo-accent text-apolo-bg'
                                : 'border-apolo-borderStrong')
                            }
                            aria-hidden="true"
                          >
                            {isSelected && <Check size={9} />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <code className="truncate font-mono text-[11.5px] font-semibold text-apolo-textPrimary">
                                {cmd.slash_command}
                              </code>
                              {cmd.display_name && cmd.display_name !== cmd.slash_command && (
                                <span className="truncate text-[10.5px] text-apolo-textTertiary">
                                  {cmd.display_name}
                                </span>
                              )}
                            </div>
                            {cmd.description && (
                              <p className="line-clamp-2 text-[10.5px] leading-snug text-apolo-textSecondary">
                                {cmd.description}
                              </p>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>

            {/* Footer */}
            <footer className="flex items-center justify-between border-t border-apolo-borderSubtle bg-apolo-surface2/40 px-4 py-2.5">
              <p className="text-[10.5px] text-apolo-textTertiary">
                {selected.size === 0 && bodyExtras.length === 0
                  ? 'Selecione skills ou clique num template'
                  : `Vai inserir: ${selected.size} skill(s)${bodyExtras.length ? ` + ${bodyExtras.length} texto(s)` : ''}`}
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md px-2.5 py-1 text-[11px] text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleInsert}
                  disabled={selected.size === 0 && bodyExtras.length === 0}
                  className="inline-flex items-center gap-1.5 rounded-md bg-apolo-accent px-3 py-1 text-[11px] font-medium text-apolo-bg transition hover:bg-apolo-accent/90 disabled:opacity-50"
                >
                  <Sparkles size={10} aria-hidden="true" />
                  Inserir
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
